import { Card as MantineCard, CardProps, useMantineColorScheme, useMantineTheme, Transition, Box } from '@mantine/core';
import { forwardRef, useMemo } from 'react';
import { useReducedMotion } from '@mantine/hooks';
import { getContrastingTextColor } from '../../../utils/color';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export type CardVariant = 'default' | 'elevated' | 'outlined' | 'subtle' | 'gradient' | 'glass';
export type HoverEffect = 'glow' | 'lift' | 'scale' | 'border' | 'none';

export interface ThemedCardProps extends CardProps {
  variant?: CardVariant;
  gradientVariant?: GradientVariant;
  gradientDirection?: number;
  hoverEffect?: HoverEffect;
  hoverable?: boolean;
  motion?: { type?: 'fade' | 'slide-up' | 'scale'; duration?: number; delay?: number };
  /** Click handler - explicitly declared for better TypeScript inference */
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  /** Mouse enter handler for hover effects */
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
  /** Mouse leave handler for hover effects */
  onMouseLeave?: React.MouseEventHandler<HTMLDivElement>;
  /** Drag over handler */
  onDragOver?: React.DragEventHandler<HTMLDivElement>;
  /** Drop handler */
  onDrop?: React.DragEventHandler<HTMLDivElement>;
}

const Card = forwardRef<HTMLDivElement, ThemedCardProps>(
  (
    {
      variant = 'default',
      gradientVariant = 'primary',
      gradientDirection = 135,
      hoverEffect = 'none',
      hoverable = false,
      motion,
      children,
      style,
      ...rest
    },
    ref
  ) => {
    const { colorScheme } = useMantineColorScheme();
    const theme = useMantineTheme();
    const isDark = colorScheme === 'dark';
    const reducedMotion = useReducedMotion();

    const gradient = theme.other.gradients[gradientVariant];

    // Calculate optimal text color for gradient background
    const gradientBg = `linear-gradient(${gradientDirection}deg, ${gradient.from}, ${gradient.to})`;
    const autoTextColor = useMemo(
      () => getContrastingTextColor(gradientBg),
      [gradientBg]
    );

    const baseCard = (() => {
      if (variant === 'gradient') {
        return (
          <MantineCard
            ref={ref}
            radius="lg"
            p="lg"
            style={{
              backgroundImage: gradientBg,
              color: style?.color || autoTextColor, // Use contrasting color, but allow override via style
              textAlign: 'left',
              transition: reducedMotion ? 'none' : 'all 0.3s ease',
              ...style,
            }}
            {...rest}
          >
            {children}
          </MantineCard>
        );
      }

      if (variant === 'glass') {
        return (
          <MantineCard
            ref={ref}
            radius="lg"
            p="lg"
            style={{
              backdropFilter: 'blur(10px)',
              backgroundColor: isDark ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.7)',
              border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
              textAlign: 'left',
              transition: reducedMotion ? 'none' : 'all 0.3s ease',
              ...style,
            }}
            {...rest}
          >
            {children}
          </MantineCard>
        );
      }

      const variantStyles: Record<Exclude<CardVariant, 'gradient' | 'glass'>, React.CSSProperties> = {
        default: {
          backgroundColor: isDark ? theme.colors.dark[6] : theme.white,
          border: `1px solid ${isDark ? theme.colors.dark[4] : theme.colors.gray[3]}`,
        },
        elevated: {
          backgroundColor: isDark ? theme.colors.dark[6] : theme.white,
          boxShadow: isDark
            ? '0 4px 20px rgba(0, 0, 0, 0.5)'
            : '0 4px 20px rgba(0, 0, 0, 0.08)',
        },
        outlined: {
          backgroundColor: 'transparent',
          border: `2px solid ${isDark ? theme.colors.dark[4] : theme.colors.gray[4]}`,
        },
        subtle: {
          backgroundColor: isDark ? theme.colors.dark[7] : theme.colors.gray[0],
          border: 'none',
        },
      };

      return (
        <MantineCard
          ref={ref}
          radius="md"
          style={{
            ...variantStyles[variant],
            textAlign: 'left',
            transition: reducedMotion ? 'none' : 'all 0.2s ease',
            cursor: hoverable || hoverEffect !== 'none' ? 'pointer' : undefined,
            ...style,
          }}
          {...rest}
        >
          {children}
        </MantineCard>
      );
    })();

    if (motion) {
      return (
        <Transition
          mounted
          transition={motion.type || 'fade'}
          duration={reducedMotion ? 0 : motion.duration ?? 200}
          timingFunction="ease-out"
        >
          {(styles) => (
            <Box style={{ ...styles, transitionDelay: `${motion.delay ?? 0}ms` }}>{baseCard}</Box>
          )}
        </Transition>
      );
    }

    return baseCard;
  }
);

Card.displayName = 'Card';

export default Card;
